import authenticatedDataverseService from './authenticatedDataverseService';

export const getHistoricalCalendarWindow = (yearsBack = 3, now = new Date()) => {
  const currentYear = now.getUTCFullYear();
  const firstYear = Math.max(2023, currentYear - Math.max(1, yearsBack) + 1);
  return {
    start: new Date(Date.UTC(firstYear, 0, 1)),
    end: new Date(Date.UTC(currentYear, 11, 31, 23, 59, 59, 999))
  };
};

class ScheduleGeneratorService {

  // Fetch complete calendar years, including already-planned future sessions in
  // the current year. Annual frequency must not treat partial years as full ones.
  async getHistoricalSessions(yearsBack = 3) {
    try {
      const { start: startDate, end: endDate } = getHistoricalCalendarWindow(yearsBack);

      const fieldMap = authenticatedDataverseService.getKnownFieldMapping();

      // Required fields (must exist on the table)
      const requiredFields = [
        fieldMap.id,
        fieldMap.name,
        fieldMap.startDate,
        fieldMap.endDate,
        fieldMap.seatsTaken,
        fieldMap.maxCapacity,
        fieldMap.location,
        fieldMap.department,
        fieldMap.classroom,
        fieldMap.billingResponsibility,
        fieldMap.sessionStatus,
        fieldMap.courseStatus,
        fieldMap.stateCode,
        fieldMap.statusCode,
        fieldMap.bundledSession,
        // The real course identity lives in the mi_course lookup. We group
        // analysis by this so the "Unique Courses" count reflects actual
        // courses (~150), not the thousands of individual session-name variants.
        '_mi_course_value'
      ].filter(Boolean);

      // Optional fields — if any cause a 400 (unknown column), they'll be removed
      // and the request retried. We try multiple instructor candidates because
      // mi_instructor may be either a text field, a lookup, or named differently.
      let optionalFields = ['mi_instructor', '_mi_instructor_value'];

      const buildSelect = () => [...new Set([...requiredFields, ...optionalFields])].join(',');

      // Paginate through all records (Dataverse max 5000 per request)
      const allSessions = [];
      let page = 0;
      const pageSize = 5000;
      let hasMore = true;
      let firstFailureSurfaced = null;

      while (hasMore) {
        let query = `$top=${pageSize}&$orderby=${fieldMap.startDate} asc`;
        query += `&$select=${buildSelect()}`;
        query += `&$filter=${fieldMap.startDate} ge ${startDate.toISOString()} and ${fieldMap.startDate} le ${endDate.toISOString()}`;

        if (page > 0) {
          query += `&$skip=${page * pageSize}`;
        }

        const endpoint = `${authenticatedDataverseService.sessionsEndpoint}?${query}`;
        console.log(`Fetching historical sessions page ${page + 1}...`);

        let result = await authenticatedDataverseService.makeDataverseRequest(endpoint);

        // If the request failed and we still have optional fields, try dropping them and retrying
        if (!result.success && optionalFields.length > 0) {
          console.warn(`Initial fetch failed (likely unknown column). Retrying without optional fields: ${optionalFields.join(', ')}`);
          optionalFields = [];
          const retryQuery = `$top=${pageSize}&$orderby=${fieldMap.startDate} asc&$select=${buildSelect()}&$filter=${fieldMap.startDate} ge ${startDate.toISOString()} and ${fieldMap.startDate} le ${endDate.toISOString()}${page > 0 ? `&$skip=${page * pageSize}` : ''}`;
          const retryEndpoint = `${authenticatedDataverseService.sessionsEndpoint}?${retryQuery}`;
          result = await authenticatedDataverseService.makeDataverseRequest(retryEndpoint);
        }

        if (result.success) {
          // Add formatted values for choice fields — fall back to raw values
          // (some columns like cr38c_department are simple text, not lookups,
          // so they have no FormattedValue annotation; choice fields like
          // mi_billingresponsibility may use either MS or OData annotation key)
          const fmt = (s, field) =>
            s[`${field}@OData.Community.Display.V1.FormattedValue`] ||
            s[`${field}@Microsoft.Dynamics.CRM.FormattedValue`] || '';

          const sessions = result.data.map(session => {
            const rawDept = session[fieldMap.department];
            const rawClassroom = session[fieldMap.classroom];
            const rawCampus = session[fieldMap.billingResponsibility];
            const stringOrEmpty = (v) => (typeof v === 'string' ? v : '');
            return {
              id: session[fieldMap.id],
              name: session[fieldMap.name] || 'Untitled',
              // Real course identity (mi_course lookup) + its display name.
              courseGuid: session._mi_course_value || null,
              courseName: fmt(session, '_mi_course_value'),
              startDate: session[fieldMap.startDate],
              endDate: session[fieldMap.endDate],
              seatsTaken: session[fieldMap.seatsTaken] || 0,
              maxCapacity: session[fieldMap.maxCapacity] || 0,
              locationGuid: session._mi_location_value,
              locationName: fmt(session, fieldMap.location),
              department: rawDept || '',
              departmentName: fmt(session, fieldMap.department) || stringOrEmpty(rawDept),
              classroom: rawClassroom || '',
              classroomName: fmt(session, fieldMap.classroom) || stringOrEmpty(rawClassroom),
              campus: rawCampus || '',
              campusName: fmt(session, fieldMap.billingResponsibility) || stringOrEmpty(rawCampus),
              courseStatus: session[fieldMap.courseStatus],
              courseStatusName: fmt(session, fieldMap.courseStatus),
              cancelled: session[fieldMap.sessionStatus] || false,
              stateCode: session[fieldMap.stateCode],
              statusCode: session[fieldMap.statusCode],
              // mi_instructor may be a text field, OR a lookup whose human-readable
              // name lives at the formatted-value annotation of _mi_instructor_value.
              // Try both shapes; keep GUID only as last-resort fallback.
              instructor:
                (typeof session.mi_instructor === 'string' && session.mi_instructor) ||
                session['_mi_instructor_value@OData.Community.Display.V1.FormattedValue'] ||
                session['_mi_instructor_value@Microsoft.Dynamics.CRM.FormattedValue'] ||
                '',
              instructorGuid: session._mi_instructor_value || null,
              isBundleParent: session[fieldMap.bundledSession] || false,
              childSessionIds: []
            };
          });

          allSessions.push(...sessions);

          hasMore = result.data.length === pageSize;
          page++;
        } else {
          console.error('Failed to fetch historical sessions:', result.error);
          if (firstFailureSurfaced === null) firstFailureSurfaced = result.error;
          hasMore = false;
        }
      }

      // If we got zero sessions due to an error, surface it instead of returning success
      if (allSessions.length === 0 && firstFailureSurfaced) {
        return { success: false, error: `Historical sessions fetch failed: ${firstFailureSurfaced}` };
      }

      // Fetch bundle relationships for parent sessions and attach child IDs
      const parentIds = allSessions.filter(s => s.isBundleParent).map(s => s.id).filter(Boolean);
      if (parentIds.length > 0) {
        try {
          const bundleMap = await authenticatedDataverseService.getBundledSessions(parentIds);
          const childIdSet = new Set();
          allSessions.forEach(s => {
            if (s.isBundleParent && bundleMap[s.id]) {
              s.childSessionIds = bundleMap[s.id];
              s.childSessionIds.forEach(id => childIdSet.add(id));
            }
          });
          // Mark child sessions
          allSessions.forEach(s => {
            if (childIdSet.has(s.id)) s.isBundleChild = true;
          });
          console.log(`Linked ${parentIds.length} bundle parents to ${childIdSet.size} child sessions`);
        } catch (bundleErr) {
          console.warn('Failed to load bundle relationships (continuing without):', bundleErr.message);
        }
      }

      // Enrich instructor names from the related mi_instructors linking table
      // (live calendar derives instructor names this way, not from a direct field).
      try {
        const sessionIds = allSessions.map(s => s.id).filter(Boolean);
        if (sessionIds.length > 0) {
          const sessionInstructorMap = await authenticatedDataverseService.getSessionInstructors(sessionIds);
          const allContactGuids = [];
          Object.values(sessionInstructorMap).forEach(list => {
            (list || []).forEach(i => { if (i.contactGuid) allContactGuids.push(i.contactGuid); });
          });
          const contactMap = allContactGuids.length
            ? await authenticatedDataverseService.getContactNames(allContactGuids)
            : {};

          let enrichedCount = 0;
          let sessionsWithLinks = 0;
          allSessions.forEach(s => {
            const list = sessionInstructorMap[s.id] || [];
            if (list.length === 0) return;
            sessionsWithLinks++;
            // Prefer primary, then first
            const primary = list.find(x => x.isPrimary) || list[0];
            const name = contactMap[primary.contactGuid];
            if (name) {
              s.instructor = name;
              enrichedCount++;
            }
            // Also expose the full list for any UI that wants multi-instructor
            s.instructors = list
              .map(x => contactMap[x.contactGuid])
              .filter(Boolean);
          });
          console.log(
            `[ScheduleGenerator] Instructor enrichment: ${sessionsWithLinks}/${allSessions.length} sessions had mi_instructors links, ` +
            `${Object.keys(contactMap).length} unique contacts resolved, ${enrichedCount} sessions got names attached.`
          );
          if (sessionsWithLinks === 0 && allSessions.length > 0) {
            console.warn(
              '[ScheduleGenerator] No mi_instructors records returned for any session. ' +
              'Either the linking table is empty for the lookback window, or _mi_session_value is not the correct relation field in this environment.'
            );
          }
        }
      } catch (instErr) {
        console.warn('Failed to enrich instructors via mi_instructors (continuing without):', instErr.message);
      }

      console.log(`Fetched ${allSessions.length} historical sessions spanning ${yearsBack} years`);
      return { success: true, data: allSessions };
    } catch (error) {
      console.error('Error fetching historical sessions:', error);
      return { success: false, error: error.message };
    }
  }

  // Fetch registration counts per session for demand analysis
  async getRegistrationCounts(sessionIds) {
    try {
      if (!sessionIds || sessionIds.length === 0) return {};

      const uniqueIds = [...new Set(sessionIds)];
      const batchSize = 25;
      const countMap = {};

      for (let i = 0; i < uniqueIds.length; i += batchSize) {
        const batch = uniqueIds.slice(i, i + batchSize);
        const sessionFilters = batch.map(id => `_mi_session_value eq ${id}`).join(' or ');

        // Count actual student registrations (not dropped)
        const endpoint = `mi_registrations?$select=mi_registrationid,_mi_session_value,_mi_student_value&$filter=(${sessionFilters}) and _mi_student_value ne null and statuscode ne 2`;

        const result = await authenticatedDataverseService.makeDataverseRequest(endpoint);

        if (result.success) {
          result.data.forEach(reg => {
            const sessionId = reg._mi_session_value;
            countMap[sessionId] = (countMap[sessionId] || 0) + 1;
          });
        }
      }

      console.log(`Fetched registration counts for ${Object.keys(countMap).length} sessions`);
      return countMap;
    } catch (error) {
      console.error('Error fetching registration counts:', error);
      return {};
    }
  }

  // Fetch all available locations and their capacities
  async getLocationsAndClassrooms() {
    try {
      const endpoint = 'mi_locations?$select=mi_locationid,mi_locationname&$filter=statecode eq 0';
      const result = await authenticatedDataverseService.makeDataverseRequest(endpoint);

      if (result.success) {
        const locations = result.data.map(loc => ({
          id: loc.mi_locationid,
          name: loc.mi_locationname
        }));
        console.log(`Fetched ${locations.length} active locations`);
        return { success: true, data: locations };
      }

      return result;
    } catch (error) {
      console.error('Error fetching locations:', error);
      return { success: false, error: error.message };
    }
  }

  // Course choices come from the exact shared Course Profile snapshot. This
  // lightweight request only refreshes active locations for the rule builder.
  async getRuleOptions() {
    try {
      const locationsResult = await this.getLocationsAndClassrooms();
      if (!locationsResult.success) return { success: false, error: locationsResult.error || 'Failed to load locations' };

      return {
        success: true,
        data: {
          locations: locationsResult.data
        }
      };
    } catch (error) {
      console.error('Error fetching scheduling rule options:', error);
      return { success: false, error: error.message };
    }
  }

  // Fetch all instructors
  async getInstructorRoster() {
    try {
      const endpoint = 'contacts?$select=contactid,fullname&$filter=statecode eq 0&$top=500';
      const result = await authenticatedDataverseService.makeDataverseRequest(endpoint);

      if (result.success) {
        const instructors = result.data.map(c => ({
          id: c.contactid,
          name: c.fullname
        }));
        console.log(`Fetched ${instructors.length} contacts for instructor roster`);
        return { success: true, data: instructors };
      }

      return result;
    } catch (error) {
      console.error('Error fetching instructor roster:', error);
      return { success: false, error: error.message };
    }
  }

  // Fetch course types, instructor qualifications, site approvals, and scheduling rules
  async getQualificationsAndRules() {
    const results = { courseTypes: [], qualifications: [], siteApprovals: [], rules: [] };

    const [ctResult, qualResult, saResult, rulesResult] = await Promise.all([
      authenticatedDataverseService.getCourseTypes(),
      authenticatedDataverseService.getInstructorQualifications(),
      authenticatedDataverseService.getSiteApprovals(),
      authenticatedDataverseService.getSchedulingRules()
    ]);

    if (ctResult.success) results.courseTypes = ctResult.data;
    if (qualResult.success) results.qualifications = qualResult.data;
    if (saResult.success) results.siteApprovals = saResult.data;
    if (rulesResult.success) results.rules = rulesResult.data;

    console.log(`Fetched ${results.courseTypes.length} course types, ${results.qualifications.length} qualifications, ${results.siteApprovals.length} site approvals, ${results.rules.length} rules`);
    return results;
  }

  // Get qualified instructors for a course, ranked by past teaching frequency
  getQualifiedInstructorsRanked(courseTypeId, qualifications, sessions) {
    const qualifiedIds = qualifications
      .filter(q => q.courseTypeId === courseTypeId && q.isActive)
      .filter(q => !q.expirationDate || new Date(q.expirationDate) > new Date())
      .map(q => q.instructorId);

    if (qualifiedIds.length === 0) return [];

    // Count past sessions per instructor for this course type
    const teachingCounts = {};
    qualifiedIds.forEach(id => { teachingCounts[id] = 0; });
    sessions.forEach(s => {
      if (qualifiedIds.includes(s.instructorId)) {
        teachingCounts[s.instructorId] = (teachingCounts[s.instructorId] || 0) + 1;
      }
    });

    return qualifiedIds
      .map(id => {
        const qual = qualifications.find(q => q.instructorId === id && q.courseTypeId === courseTypeId);
        return {
          instructorId: id,
          instructorName: qual?.instructorName || '',
          pastSessionCount: teachingCounts[id] || 0
        };
      })
      .sort((a, b) => b.pastSessionCount - a.pastSessionCount);
  }

  // Get the CCA max capacity for a location + course type from site approvals
  getSiteMaxCapacity(locationId, courseTypeId, siteApprovals) {
    const approval = siteApprovals.find(
      sa => sa.locationId === locationId && sa.courseTypeId === courseTypeId
    );
    return approval ? approval.maxStudents : null;
  }

  // Evaluate scheduling rules against a proposed session
  evaluateRules(proposedSession, existingSessions, rules, locations) {
    const violations = [];

    for (const rule of rules) {
      if (!rule.isEnabled) continue;

      switch (rule.ruleType) {
        case 1: // NoSimultaneous
          violations.push(...this._checkNoSimultaneous(proposedSession, existingSessions, rule, locations));
          break;
        case 2: // MinGapDays
          violations.push(...this._checkMinGap(proposedSession, existingSessions, rule));
          break;
        default:
          break;
      }
    }

    return violations;
  }

  _checkNoSimultaneous(proposed, existing, rule, locations) {
    const violations = [];
    const proposedStart = new Date(proposed.startDate);
    const proposedEnd = new Date(proposed.endDate);

    // Determine which locations are in scope based on region
    const inScopeLocationIds = this._getLocationsByRegion(rule.regionScope, locations);

    if (inScopeLocationIds.length > 0 && !inScopeLocationIds.includes(proposed.locationGuid)) {
      return []; // Proposed session is not in the region this rule covers
    }

    for (const session of existing) {
      if (inScopeLocationIds.length > 0 && !inScopeLocationIds.includes(session.locationGuid)) continue;

      // Check course type filter
      if (rule.courseType1Id && proposed.courseTypeId !== rule.courseType1Id) continue;
      if (rule.courseType1Id && session.courseTypeId !== rule.courseType1Id) continue;

      const sessionStart = new Date(session.startDate);
      const sessionEnd = new Date(session.endDate);

      if (proposedStart <= sessionEnd && proposedEnd >= sessionStart) {
        violations.push({
          ruleId: rule.id,
          ruleName: rule.name,
          severity: 'error',
          message: `Conflicts with "${session.name}" at ${session.locationName} (${rule.name})`
        });
      }
    }

    return violations;
  }

  _checkMinGap(proposed, existing, rule) {
    const violations = [];
    const proposedStart = new Date(proposed.startDate);
    const proposedEnd = new Date(proposed.endDate);
    const gapMs = (rule.minGapDays || 7) * 24 * 60 * 60 * 1000;

    for (const session of existing) {
      // Check course type match
      if (rule.courseType1Id && proposed.courseTypeId !== rule.courseType1Id) continue;
      if (rule.courseType2Id && session.courseTypeId !== rule.courseType2Id) continue;

      // Check location match if specified
      if (rule.location1Id && proposed.locationGuid !== rule.location1Id) continue;
      if (rule.location2Id && session.locationGuid !== rule.location2Id) continue;

      const sessionStart = new Date(session.startDate);
      const sessionEnd = new Date(session.endDate);

      const gapBefore = proposedStart - sessionEnd;
      const gapAfter = sessionStart - proposedEnd;
      // eslint-disable-next-line no-unused-vars
      const actualGap = Math.min(
        Math.abs(gapBefore),
        Math.abs(gapAfter)
      );

      if (gapBefore > 0 && gapBefore < gapMs) {
        violations.push({
          ruleId: rule.id,
          ruleName: rule.name,
          severity: 'warning',
          message: `Only ${Math.floor(gapBefore / (24*60*60*1000))} days after "${session.name}" — minimum is ${rule.minGapDays} days`
        });
      } else if (gapAfter > 0 && gapAfter < gapMs) {
        violations.push({
          ruleId: rule.id,
          ruleName: rule.name,
          severity: 'warning',
          message: `Only ${Math.floor(gapAfter / (24*60*60*1000))} days before "${session.name}" — minimum is ${rule.minGapDays} days`
        });
      }
    }

    return violations;
  }

  _getLocationsByRegion(regionScope, locations) {
    if (!regionScope || !locations) return [];
    const regionName = typeof regionScope === 'number'
      ? { 1: 'WestCoast', 2: 'EastCoast', 3: 'OCONUS', 4: 'All' }[regionScope]
      : regionScope;
    if (regionName === 'All') return [];
    return locations.filter(l => l.region === regionName).map(l => l.id);
  }

  // Build the complete analysis dataset for the algorithm
  async buildAnalysisDataset(yearsBack = 3, onProgress = null) {
    const report = (phase, message) => {
      console.log(`[ScheduleGenerator] ${phase}: ${message}`);
      if (onProgress) onProgress(phase, message);
    };

    report('sessions', 'Fetching historical sessions...');
    const sessionsResult = await this.getHistoricalSessions(yearsBack);
    if (!sessionsResult.success) return sessionsResult;

    const sessions = sessionsResult.data;
    report('sessions', `Loaded ${sessions.length} sessions`);

    report('registrations', 'Fetching registration data...');
    const sessionIds = sessions.map(s => s.id).filter(Boolean);
    const registrationCounts = await this.getRegistrationCounts(sessionIds);
    report('registrations', `Loaded registration data for ${Object.keys(registrationCounts).length} sessions`);

    // Attach actual registration counts to sessions
    const enrichedSessions = sessions.map(s => ({
      ...s,
      actualEnrolled: registrationCounts[s.id] || s.seatsTaken || 0,
      fillRate: s.maxCapacity > 0
        ? ((registrationCounts[s.id] || s.seatsTaken || 0) / s.maxCapacity)
        : 0
    }));

    report('locations', 'Fetching locations...');
    const locationsResult = await this.getLocationsAndClassrooms();
    const locations = locationsResult.success ? locationsResult.data : [];

    // Build GUID → name lookup and backfill any session.locationName that came back blank
    const locationNameMap = {};
    locations.forEach(loc => { if (loc.id) locationNameMap[loc.id] = loc.name; });
    enrichedSessions.forEach(s => {
      if ((!s.locationName || s.locationName === '') && s.locationGuid && locationNameMap[s.locationGuid]) {
        s.locationName = locationNameMap[s.locationGuid];
      }
    });
    report('locations', `Loaded ${locations.length} locations and backfilled session names`);

    report('instructors', 'Fetching instructor roster...');
    const instructorsResult = await this.getInstructorRoster();
    const instructors = instructorsResult.success ? instructorsResult.data : [];

    report('qualifications', 'Fetching qualifications, site approvals, and scheduling rules...');
    const qualAndRules = await this.getQualificationsAndRules();
    report('qualifications', `Loaded ${qualAndRules.courseTypes.length} course types, ${qualAndRules.qualifications.length} quals, ${qualAndRules.siteApprovals.length} site approvals, ${qualAndRules.rules.length} rules`);

    report('complete', 'Analysis dataset ready');

    return {
      success: true,
      data: {
        sessions: enrichedSessions,
        locations,
        instructors,
        courseTypes: qualAndRules.courseTypes,
        qualifications: qualAndRules.qualifications,
        siteApprovals: qualAndRules.siteApprovals,
        schedulingRules: qualAndRules.rules,
        dateRange: getHistoricalCalendarWindow(yearsBack)
      }
    };
  }

  // Write approved draft sessions to Dataverse
  async publishDraftSessions(draftSessions) {
    // Publishing is intentionally unavailable. Keep this service-level block
    // even though the UI controls are disabled so no Schedule Generator path
    // contains a Dataverse session-write implementation.
    const msg = 'Publishing Schedule Generator drafts to Power Apps is disabled. No records were sent.';
    console.warn(msg);
    return {
      created: 0,
      failed: draftSessions.length,
      errors: draftSessions.map(draft => ({ session: draft.name || draft.id, error: msg })),
      blocked: true
    };
  }

  // Fetch course list prices from the mi_course catalog for revenue projection.
  //
  // Dataverse holds the price on mi_course, but the exact logical name isn't
  // known to this app, so we probe one record, auto-discover the money/price
  // field, then fetch every course's id, name and price. The chosen field name
  // is returned so the UI can surface it for verification.
  //
  // @returns {Promise<{success, priceField, nameField, byGuid, byName, candidates, count, error?}>}
  async getCoursePrices() {
    // Preferred logical names, in order, if present on the entity.
    const PREFERRED_PRICE_FIELDS = [
      'mi_price', 'mi_cost', 'mi_courseprice', 'mi_coursecost',
      'mi_listprice', 'mi_standardprice', 'mi_fee', 'mi_tuition', 'mi_rate'
    ];
    const PRICE_PATTERN = /(price|cost|fee|tuition|rate|amount)/i;

    const normName = (n) => String(n || '').trim().toLowerCase().replace(/\s+/g, ' ');

    // 1. Probe one record to discover fields. Try plural then singular set name.
    let entitySet = 'mi_courses';
    let probe = await authenticatedDataverseService.makeDataverseRequest(`${entitySet}?$top=1`);
    if (!probe.success || !Array.isArray(probe.data) || probe.data.length === 0) {
      entitySet = 'mi_course';
      probe = await authenticatedDataverseService.makeDataverseRequest(`${entitySet}?$top=1`);
    }
    if (!probe.success || !Array.isArray(probe.data) || probe.data.length === 0) {
      return {
        success: false,
        error: probe.error || 'No course records found',
        priceField: null, nameField: null, byGuid: {}, byName: {}, candidates: [], count: 0
      };
    }

    const sample = probe.data[0];
    const fields = Object.keys(sample).filter(k => !k.includes('@'));

    // Money-like candidate fields: numeric value, name matches the pattern,
    // and not a currency-base/_value shadow field.
    const candidates = fields.filter(f =>
      PRICE_PATTERN.test(f) &&
      !f.endsWith('_base') &&
      !f.startsWith('_') &&
      (typeof sample[f] === 'number' || sample[f] === null)
    );

    const priceField =
      PREFERRED_PRICE_FIELDS.find(f => fields.includes(f)) ||
      candidates[0] ||
      null;

    // Name field: mi_course primary name is usually mi_name.
    const nameField = ['mi_name', 'mi_coursename', 'mi_title'].find(f => fields.includes(f)) || 'mi_name';
    const idField = ['mi_courseid'].find(f => fields.includes(f)) || 'mi_courseid';

    if (!priceField) {
      return {
        success: false,
        error: 'Could not identify a price field on mi_course',
        priceField: null, nameField, byGuid: {}, byName: {}, candidates, count: 0
      };
    }

    // 2. Fetch every course's id, name and price (paginated).
    const select = [...new Set([idField, nameField, priceField])].join(',');
    const result = await authenticatedDataverseService.fetchAllPages(`${entitySet}?$select=${select}`, { failOnPageError: true });
    if (!result.success) {
      return {
        success: false, error: result.error,
        priceField, nameField, byGuid: {}, byName: {}, candidates, count: 0
      };
    }

    const byGuid = {};
    const byName = {};
    result.data.forEach(c => {
      const rawPrice = c[priceField];
      if (rawPrice === null || rawPrice === undefined || rawPrice === '') return;
      const price = Number(rawPrice);
      if (!Number.isFinite(price)) return;
      const guid = c[idField];
      const name = c[nameField];
      if (guid) byGuid[guid] = price;
      if (name) byName[normName(name)] = price;
    });

    console.log(`Loaded ${Object.keys(byGuid).length} course prices from ${entitySet}.${priceField}`, { candidates });

    if (Object.keys(byGuid).length === 0 && Object.keys(byName).length === 0) {
      return {
        success: false,
        error: `No usable prices found in ${entitySet}.${priceField}`,
        priceField, nameField, byGuid, byName, candidates, count: 0
      };
    }

    return {
      success: true,
      priceField, nameField,
      byGuid, byName, candidates,
      count: Object.keys(byGuid).length
    };
  }
}

const scheduleGeneratorService = new ScheduleGeneratorService();
export default scheduleGeneratorService;
